/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import type { AbsoluteURL } from '@poweralpha/common';
import { matchPath } from 'react-router-dom';

/**
 * Paths the Project Simulate window renders. Its own pages live below `/projects/:id/simulate`;
 * the older batch, single-run results, FIMO config and FIMO app paths open in the window and
 * redirect to the matching page.
 */
export const PROJECT_SIMULATE_ENTRY_PATHS = {
  project: '/projects/:projectId/simulate/*',
  noProject: '/projects/simulate/*',
  legacyBatch: '/projects/:projectId/batch/*',
  legacyResults: '/projects/:projectId/results/*',
  legacyFimoConfig: '/projects/:projectId/configurations/*',
  legacyFimo: '/fimo/*',
} as const;

export const PROJECT_SIMULATE_NO_PROJECT_PATH = '/projects/simulate' as AbsoluteURL;

/** Tabs of the toolbar switch. */
export type ProjectSimulateTab = 'runs' | 'inputs' | 'compare';

export type ProjectSimulateView =
  | 'runs'
  | 'new'
  | 'run'
  | 'sweep'
  | 'mergedSweeps'
  | 'compare'
  | 'inputs'
  | 'financeModel';

export const runReportTabs = [
  'overview',
  'energy',
  'costs',
  'finance',
  'cash-flow',
  'inputs',
] as const;
export type RunReportTab = (typeof runReportTabs)[number];

export const inputKinds = ['finance', 'pv', 'bess', 'curves'] as const;
export type InputKind = (typeof inputKinds)[number];

export const runTypeFilters = ['all', 'single', 'sweep'] as const;
export type RunTypeFilter = (typeof runTypeFilters)[number];

export const projectSimulatePath = (projectId: string, sub = '') =>
  `/projects/${projectId}/simulate${sub ? `/${sub.replace(/^\//, '')}` : ''}` as AbsoluteURL;

const withSearch = (path: string, params: Record<string, string | undefined | null>) => {
  const search = new URLSearchParams(
    Object.entries(params).filter((entry): entry is [string, string] => !!entry[1]),
  ).toString();
  return (search ? `${path}?${search}` : path) as AbsoluteURL;
};

export const runsPath = (projectId: string, type?: RunTypeFilter) =>
  withSearch(projectSimulatePath(projectId), { type: type === 'all' ? undefined : type });

export const newRunPath = (
  projectId: string,
  from?: {
    scenario?: string;
    batch?: string;
    promote?: string;
    cell?: string;
    /** Configs to start with, each `{ id, version }` (e.g. those of a run that failed). */
    pv?: ConfigVersion | null;
    ess?: ConfigVersion | null;
    fimo?: ConfigVersion | null;
  },
) =>
  withSearch(projectSimulatePath(projectId, 'new'), {
    from: from?.scenario,
    batch: from?.batch,
    promote: from?.promote,
    cell: from?.cell,
    pv: from?.pv ? `${from.pv.id}:${from.pv.version}` : undefined,
    ess: from?.ess ? `${from.ess.id}:${from.ess.version}` : undefined,
    fimo: from?.fimo ? `${from.fimo.id}:${from.fimo.version}` : undefined,
  });

type ConfigVersion = { id: string; version: number };

export const runReportPath = (projectId: string, scenarioId: string, tab?: RunReportTab) =>
  projectSimulatePath(projectId, `runs/${scenarioId}${tab && tab !== 'overview' ? `/${tab}` : ''}`);

export const sweepPath = (projectId: string, batchId: string, cell?: string | number) =>
  withSearch(projectSimulatePath(projectId, `sweeps/${batchId}`), {
    cell: cell === undefined ? undefined : String(cell),
  });

export const mergedSweepsPath = (projectId: string, ids: string[], type: string) =>
  withSearch(projectSimulatePath(projectId, 'sweeps/merged'), { ids: ids.join(','), type });

export const comparePath = (projectId: string, scenarioIds: string[] = []) =>
  withSearch(projectSimulatePath(projectId, 'compare'), {
    runs: scenarioIds.length ? scenarioIds.join(',') : undefined,
  });

export const inputsPath = (projectId: string, kind?: InputKind) =>
  withSearch(projectSimulatePath(projectId, 'inputs'), {
    kind: kind === 'finance' ? undefined : kind,
  });

export const financeModelPath = (projectId: string, configId?: string, version?: number) =>
  withSearch(projectSimulatePath(projectId, 'inputs/finance'), {
    config: configId,
    version: version === undefined ? undefined : String(version),
  });

/** Editor of a config a run used: PV and BESS open in their apps, finance models here. */
export const configPath = (
  projectId: string,
  kind: 'pv' | 'ess' | 'fimo',
  id: string,
  version: number,
): AbsoluteURL => {
  switch (kind) {
    case 'pv':
      return `/pv/costing/simulate?config=${id}&version=${version}` as AbsoluteURL;
    case 'ess':
      return `/ess/costing/simulate?config=${id}&version=${version}` as AbsoluteURL;
    default:
      return financeModelPath(projectId, id, version);
  }
};

/** The page at `pathname` (relative to the window's project), e.g. `/runs/abc/finance`. */
export function getProjectSimulateView(pathname: string): {
  view: ProjectSimulateView;
  scenarioId?: string;
  batchId?: string;
  tab?: RunReportTab;
  /** The path names a run report tab that does not exist (any more, e.g. `validation`). */
  unknownTab?: true;
} {
  const sub = pathname.replace(/^\/projects\/[^/]+\/simulate/, '').replace(/\/$/, '');
  if (!sub) return { view: 'runs' };
  if (sub === '/new') return { view: 'new' };
  if (sub === '/compare') return { view: 'compare' };
  if (sub === '/inputs') return { view: 'inputs' };
  if (sub === '/inputs/finance') return { view: 'financeModel' };
  if (sub === '/sweeps/merged') return { view: 'mergedSweeps' };
  const sweep = matchPath('/sweeps/:batchId', sub);
  if (sweep?.params.batchId) return { view: 'sweep', batchId: sweep.params.batchId };
  const run = matchPath('/runs/:scenarioId/*', sub);
  if (run?.params.scenarioId) {
    const segment = run.params['*']?.split('/')[0];
    const tab = runReportTabs.find((value) => value === segment);
    // Unknown and removed tabs (`/validation`) open the overview.
    return tab || !segment
      ? { view: 'run', scenarioId: run.params.scenarioId, tab: tab ?? 'overview' }
      : { view: 'run', scenarioId: run.params.scenarioId, tab: 'overview', unknownTab: true };
  }
  return { view: 'runs' };
}

/** The toolbar tab a view sits below. */
export const getProjectSimulateTab = (view: ProjectSimulateView): ProjectSimulateTab => {
  switch (view) {
    case 'inputs':
    case 'financeModel':
      return 'inputs';
    case 'compare':
      return 'compare';
    default:
      return 'runs';
  }
};

/**
 * The page the toolbar's Up button leads to: the runs list for pages below it and for the other
 * tabs (the window's root), the sweeps for a sweep, the finance models for a finance model. None
 * on the runs list itself.
 */
export const getProjectSimulateParentPath = (
  view: ProjectSimulateView,
  projectId: string,
): AbsoluteURL | undefined => {
  switch (view) {
    case 'runs':
      return undefined;
    case 'sweep':
    case 'mergedSweeps':
      return runsPath(projectId, 'sweep');
    case 'financeModel':
      return inputsPath(projectId);
    default:
      return runsPath(projectId);
  }
};

// Scenario result tabs of the old single-run page, by their path segment.
const legacyRunTabs: Record<string, RunReportTab> = {
  engineering: 'energy',
  summary: 'overview',
  finance: 'finance',
};

const legacyBatchPath = (path: string, params: URLSearchParams) => {
  const batch = matchPath('/projects/:projectId/batch/*', path);
  if (!batch?.params.projectId) return undefined;
  const { projectId } = batch.params;
  const rest = batch.params['*'] ?? '';
  if (!rest) return runsPath(projectId, 'sweep');
  if (rest === 'simulate') return newRunPath(projectId, { batch: params.get('id') ?? undefined });
  if (rest === 'merged-results') {
    return mergedSweepsPath(
      projectId,
      (params.get('ids') ?? '').split(',').filter(Boolean),
      params.get('type') ?? '',
    );
  }
  return sweepPath(projectId, rest.split('/')[0]);
};

const legacyResultsPath = (path: string, params: URLSearchParams) => {
  const results = matchPath('/projects/:projectId/results/*', path);
  if (!results?.params.projectId) return undefined;
  const { projectId } = results.params;
  const rest = results.params['*'] ?? '';
  if (!rest) return runsPath(projectId, 'single');
  if (rest === 'compare') return comparePath(projectId, params.getAll('scenario'));
  const [scenarioId, tab] = rest.split('/');
  return runReportPath(projectId, scenarioId, tab ? legacyRunTabs[tab] : undefined);
};

const legacyConfigsPath = (path: string, params: URLSearchParams) => {
  const configs = matchPath('/projects/:projectId/configurations/*', path);
  if (!configs?.params.projectId) return undefined;
  const version = params.get('version');
  return financeModelPath(
    configs.params.projectId,
    params.get('config') ?? undefined,
    version ? Number.parseInt(version, 10) : undefined,
  );
};

/**
 * The Project Simulate page for an older path (batch, single-run results, FIMO configs), or
 * `undefined` when `pathname` is not one. FIMO app paths have no project and are resolved by the
 * window (`fimoLegacyTarget`).
 */
export function getLegacyProjectSimulatePath(
  pathname: string,
  search: string,
): AbsoluteURL | undefined {
  const params = new URLSearchParams(search);
  const path = pathname.replace(/\/$/, '');
  return (
    legacyBatchPath(path, params) ??
    legacyResultsPath(path, params) ??
    legacyConfigsPath(path, params)
  );
}

/** What an old FIMO app path asks for: a finance model, or the finance models list. */
export function fimoLegacyTarget(pathname: string, search: string) {
  if (!matchPath('/fimo/*', pathname)) return undefined;
  const params = new URLSearchParams(search);
  const configId = params.get('config');
  const version = params.get('version');
  return {
    configId: configId ?? undefined,
    version: version ? Number.parseInt(version, 10) : undefined,
    editor: /\/configurations\/new/.test(pathname),
  };
}
